import React, { useEffect, useRef, useState } from "react";
import { Wheat, Gauge } from "lucide-react";
import { Hud, Petunjuk, useCountdown } from "../Hud";
import { PetakSawah, KOLOM, BARIS } from "./PetakSawah";
import { Juragan } from "./Juragan";
import { sfx } from "../../../lib/sfx";

const NAMA_JURAGAN = ["Juragan Karto", "Pak Haji Sapto", "Juragan Wiryo", "Pak Dhe Harjo", "Juragan Mulyono", "Pak Haji Darmo"];
// Komentar juragan: sarkas saat padi dimakan hama / layu / kering dibiarkan, pujian saat panen beruntun, pembuka & ganti tahap
const Q = {
  mulai: ["Sawah ini warisan, Le. Jangan diwariskan ke tikus.", "Saya cuma nonton. Yang kotor kakinya sampeyan.", "Padi itu ditanam, disiram, dipanen. Bukan dipandangi.", "Target hari ini banyak. Jangan bikin saya ngelus dada."],
  habis: ["Tikusnya kenyang, saya yang lapar.", "Itu sawah atau warung gratis buat hama?", "Padinya dimakan tikus. Gajinya dimakan apa, ya?", "Sampeyan nanem buat siapa sebenarnya, Le? Buat tikus?", "Burungnya sampai bawa bekal pulang.", "Sawah saya jadi tempat wisata kuliner hama.", "Hamanya datang, sampeyan diam. Kompak sekali.", "Kalau hama bisa bayar sewa, saya nggak protes.", "Padi satu petak hilang. Hati saya juga.", "Tikusnya sudah hafal jadwal sampeyan melamun.", "Itu tadi ketukannya kemana, Le? Ke angan-angan?", "Hamanya makan lebih cepat dari sampeyan mikir."],
  layu: ["Padinya nunggu dipanen sampai pensiun.", "Sudah kuning, dilihatin aja. Dikira lukisan?", "Padi layu itu bukan hiasan, Le.", "Itu padinya nangis kering nunggu sampeyan.", "Panen itu diketuk, bukan ditunggu sampai jadi jerami.", "Layu lagi. Sampeyan memang telaten... telat terus.", "Padinya sudah menguning dari tadi, sampeyan baru datang sekarang."],
  kering: ["Petaknya retak, hati saya ikut retak.", "Sawah kering kok dibiarin. Nunggu hujan apa nunggu mukjizat?", "Airnya habis, Le. Semangat sampeyan juga?"],
  puas: ["Nah, begini. Baru pantas disebut petani.", "Lumayan, Le. Saya mulai nggak malu punya pegawai.", "Panennya lancar. Jangan GR dulu, masih ada hama.", "Bagus! Nanti bonusnya... saya pikir-pikir dulu.", "Tangan sampeyan rupanya bisa cepat juga kalau mau.", "Padi menumpuk. Akhirnya ada kabar baik buat lumbung saya."],
  tahap: { 1: "Burung pipit mulai datang. Jangan cuma lihat tikus.", 2: "Musim kering, Le. Petak retak harus disiram lagi." },
};
function pilihBaru(arr, rnd, riwayat) {
  let k = arr[Math.floor(rnd() * arr.length)];
  for (let i = 0; i < 8 && riwayat.includes(k); i++) k = arr[Math.floor(rnd() * arr.length)];
  riwayat.push(k); if (riwayat.length > 4) riwayat.shift();
  return k;
}

const DURASI = 60, TARGET = 32, TUMBUH_MS = 3000, TICK = 100;
// Tahap kesulitan dalam satu shift (detik berjalan): 1 = tikus saja · 2 = + burung pipit (makan lebih cepat) ·
// 3 = + kekeringan (petak basah mengering, harus disiram lagi). Level profesi mempercepat semua.
const TAHAP = [
  { mulai: 0, nama: "Tahap 1", pesan: null, hama: 4200, layu: 4800, burung: false, kering: 0 },
  { mulai: 20, nama: "Tahap 2", pesan: "Tahap 2 · burung pipit datang!", hama: 3200, layu: 4000, burung: true, kering: 0 },
  { mulai: 40, nama: "Tahap 3", pesan: "Tahap 3 · musim kering!", hama: 2600, layu: 3400, burung: true, kering: 0.35 },
];
const MAKAN_MS = { tikus: 2500, burung: 1700 };

// Mini game Petani — Sawah: tiap petak bersiklus kosong → ketuk tanam (benih) → ketuk siram (basah, tumbuh ±3 dtk) → siap panen
// (menguning) → ketuk panen (+1 padi); dibiarkan → layu (ketuk untuk bersihkan). Hama (tikus; burung di tahap 2+) hinggap di petak
// tumbuh/siap — ketuk untuk usir, kalau tidak padi habis. Tahap 3: petak basah bisa mengering (tanah retak) → ketuk untuk siram ulang.
// Skor = panen / 32 × 100. Level ↑ → jeda hama & waktu layu lebih singkat.
export function Sawah({ shift, onSelesai }) {
  const lv = shift.level || 1;
  const ref = useRef(null);
  if (!ref.current) ref.current = { petak: Array.from({ length: KOLOM * BARIS }, () => ({ fase: "kosong", t: 0, hama: null, hamaT: 0 })), panen: 0, usir: 0, selesai: false, nextHama: 0, nextKering: 0, n: 0, k: 0, tahap: 0, mulai: 0, riwayat: [], beruntun: 0, diamSejak: 0 };
  const [petak, setPetak] = useState(ref.current.petak);
  const [stat, setStat] = useState({ panen: 0, usir: 0, tahap: 0 });
  const [pops, setPops] = useState([]);
  const [info, setInfo] = useState(null);
  const rnd = () => { const x = Math.sin(shift.seed + (ref.current.n++) * 97.3) * 10000; return x - Math.floor(x); };
  if (!ref.current.nama) ref.current.nama = NAMA_JURAGAN[Math.floor(rnd() * NAMA_JURAGAN.length)];
  const [ucapan, setUcapan] = useState(() => ({ teks: pilihBaru(Q.mulai, rnd, ref.current.riwayat), nada: "pesan" }));
  // Juragan berkomentar paling cepat tiap 1.8 dtk agar tidak berisik; sindiran memutus hitungan panen beruntun
  const komentar = (daftar, nada) => { const r = ref.current, now = performance.now(); if (now - r.diamSejak < 1800) return; r.diamSejak = now; if (nada === "kecewa") r.beruntun = 0; setUcapan({ teks: pilihBaru(daftar, rnd, r.riwayat), nada }); };
  const T = () => TAHAP[ref.current.tahap];
  const skala = Math.max(0.55, 1 - (lv - 1) * 0.06);                 // level profesi mempercepat
  const jedaHama = () => Math.max(1500, (T().hama + rnd() * 1800) * skala);
  const layuMs = () => Math.max(2200, T().layu * skala);

  function akhiri() {
    const r = ref.current;
    if (r.selesai) return;
    r.selesai = true;
    onSelesai(Math.min(100, Math.round((r.panen / TARGET) * 100)));
  }
  const sisa = useCountdown(DURASI, akhiri);
  const pop = (i, teks, warna) => setPops(ps => [...ps.slice(-5), { i, teks, warna, k: ref.current.k++ }]);

  useEffect(() => {
    const r = ref.current;
    r.mulai = performance.now(); r.nextHama = r.mulai + 5000; r.nextKering = r.mulai + 3000;
    const t = setInterval(() => {
      if (r.selesai) return;
      const now = performance.now(), detik = (now - r.mulai) / 1000;
      // naik tahap
      const th = TAHAP.reduce((acc, s, idx) => (detik >= s.mulai ? idx : acc), 0);
      if (th !== r.tahap) { r.tahap = th; setInfo(TAHAP[th].pesan); setTimeout(() => setInfo(null), 2200); setUcapan({ teks: Q.tahap[th], nada: "pesan" }); r.diamSejak = now; }
      const P = r.petak.map(p => ({ ...p }));
      P.forEach((p, i) => {
        if (p.hama && now - p.hamaT > MAKAN_MS[p.hama]) { P[i] = { fase: "kosong", t: now, hama: null, hamaT: 0 }; pop(i, "Habis!", "#ffb4b4"); komentar(Q.habis, "kecewa"); }
        else if (p.fase === "basah" && now - p.t > TUMBUH_MS) { p.fase = "siap"; p.t = now; }
        else if (p.fase === "siap" && now - p.t > layuMs()) { p.fase = "layu"; p.t = now; p.hama = null; komentar(Q.layu, "kecewa"); }
        else if (p.fase === "kering" && now - p.t > 4000) { p.t = now; komentar(Q.kering, "kecewa"); }
      });
      if (now > r.nextHama) {
        const calon = P.map((p, i) => (!p.hama && (p.fase === "basah" || p.fase === "siap") ? i : -1)).filter(i => i >= 0);
        if (calon.length) {
          const i = calon[Math.floor(rnd() * calon.length)];
          P[i].hama = T().burung && P[i].fase === "siap" && rnd() < 0.55 ? "burung" : "tikus"; P[i].hamaT = now;
        }
        r.nextHama = now + jedaHama();
      }
      if (T().kering > 0 && now > r.nextKering) {
        const calon = P.map((p, i) => (p.fase === "basah" && !p.hama && (now - p.t) < TUMBUH_MS * 0.7 ? i : -1)).filter(i => i >= 0);
        if (calon.length && rnd() < T().kering) { const i = calon[Math.floor(rnd() * calon.length)]; P[i].fase = "kering"; P[i].progres = Math.min(1, (now - P[i].t) / TUMBUH_MS); P[i].t = now; pop(i, "Kering!", "#ffd9a8"); }
        r.nextKering = now + 2500 * skala;
      }
      r.petak = P; setPetak(P); setStat(s => (s.tahap === r.tahap ? s : { ...s, tahap: r.tahap }));
    }, TICK);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  function tap(i) {
    const r = ref.current;
    if (r.selesai) return;
    const now = performance.now(), P = r.petak.map(p => ({ ...p })), p = P[i];
    if (p.hama) { p.hama = null; p.hamaT = 0; r.usir += 1; pop(i, "Usir!", "#ffd66b"); sfx.tak(); }
    else if (p.fase === "kosong") { p.fase = "benih"; p.t = now; pop(i, "Tanam"); }
    else if (p.fase === "benih") { p.fase = "basah"; p.t = now; pop(i, "Siram", "#bfe6ff"); }
    else if (p.fase === "kering") { p.fase = "basah"; p.t = now - (p.progres || 0) * TUMBUH_MS * 0.5; pop(i, "Siram", "#bfe6ff"); }
    else if (p.fase === "siap") { p.fase = "kosong"; p.t = now; r.panen += 1; r.beruntun += 1; pop(i, "+1 Padi", "#ffe08a"); sfx.krak(); if (r.beruntun % 5 === 0) komentar(Q.puas, "puas"); }
    else if (p.fase === "layu") { p.fase = "kosong"; p.t = now; pop(i, "Bersih", "#d9d9d9"); }
    else return;
    r.petak = P; setPetak(P); setStat({ panen: r.panen, usir: r.usir, tahap: r.tahap });
  }

  const now = performance.now(), LAYU = layuMs();
  const tampil = petak.map(p => ({ fase: p.fase, hama: p.hama, progres: p.fase === "basah" ? Math.min(1, (now - p.t) / TUMBUH_MS) : 0, peringatan: p.fase === "siap" && LAYU - (now - p.t) < 1200 }));
  const ada = f => petak.some(p => p.fase === f);
  const petunjuk = info || (petak.some(p => p.hama) ? "Usir hama! Ketuk tikus / burungnya" : ada("kering") ? "Petak mengering! Siram ulang" : ada("siap") ? "Panen padi yang menguning" : ada("benih") ? "Siram benih yang sudah ditanam" : ada("layu") ? "Bersihkan padi yang layu" : "Ketuk petak kosong untuk menanam");

  return (
    <>
      <Hud sisa={sisa} durasi={DURASI} poin={stat.panen} extra={[[Wheat, "#d9a21b", `${stat.panen}/${TARGET}`], [Gauge, stat.tahap === 2 ? "#dc2626" : stat.tahap === 1 ? "#c27a1c" : "#2e9e5b", TAHAP[stat.tahap].nama]]} />
      <div style={{ position: "absolute", left: 0, right: 0, top: "calc(env(safe-area-inset-top, 0px) + 124px)", height: 150 }}>
        <Juragan nama={ref.current.nama} nada={ucapan.nada} ucapan={ucapan.teks} />
      </div>
      <PetakSawah petak={tampil} onTap={tap} pops={pops} style={{ top: "57%" }} />
      <Petunjuk>{petunjuk}</Petunjuk>
    </>
  );
}
